/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import {
    forwardRef,
    CSSProperties,
    ReactNode,
    ChangeEvent,
    ChangeEventHandler,
    FocusEventHandler,
    FormEventHandler,
    KeyboardEventHandler,
} from 'react';
import FormControl from 'react-bootstrap/FormControl';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import classNames from 'classnames';

import { Validation } from 'common/components/form/FormValidationDisplay';
import { getValidationState } from 'common/utils/validation-utils';

import {
    LabelWithRequired,
    resolveComponentLocator,
    joinDataTestId,
} from '@motili/ui';
import styles from './FormTextInput.module.scss';
import { StyledInput } from './StyledInput';
import { StyledInputMask } from './StyledInputMask';

export interface FormTextInputProps {
    style?: CSSProperties;
    labelStyle?: CSSProperties;
    className?: string;
    controlId?: string;
    name?: string;
    label?: string;
    placeholder?: string;
    validation?: Validation;
    bsSize?: 'large' | 'small';
    required?: boolean;
    mask?: string;
    disabled?: boolean;
    inputGroupBefore?: ReactNode;
    inputGroupAfter?: ReactNode;
    saveUnmasked?: boolean;
    maxLength?: number;
    value?: string | number | readonly string[];
    type?: string;
    autocomplete?: string;
    dataTestId?: string;
    onChange?:
        | ChangeEventHandler<HTMLInputElement>
        | FormEventHandler<typeof FormControl>
        | undefined;
    autoFocus?: boolean;
    onBlur?:
        | FocusEventHandler<typeof FormControl>
        | FocusEventHandler<HTMLInputElement>;

    onKeyPress?:
        | KeyboardEventHandler<HTMLInputElement>
        | KeyboardEventHandler<typeof FormControl>;

    unitSuffix?: ReactNode;
    onFocus?: FocusEventHandler<typeof FormControl>;
    hideIcon?: boolean;
}

type Ref = HTMLInputElement;

const FormTextInput = forwardRef<Ref, FormTextInputProps>(
    function FormTextInput(
        {
            style = {},
            className = '',
            validation = { state: null },
            saveUnmasked = false,
            ...props
        },
        ref
    ) {
        const prefixTestId =
            props.dataTestId ||
            props.controlId ||
            props.name ||
            'form-text-input';

        const inputPrefixTestId = joinDataTestId([
            prefixTestId,
            resolveComponentLocator('', 'input', prefixTestId),
        ]);

        const { isInvalid, isValid, validationFeedbackType } =
            getValidationState(validation);

        return (
            <Form.Group
                style={style}
                className={className}
                controlId={props.controlId || props.name}
            >
                {props.label && (
                    <LabelWithRequired
                        dataTestId={prefixTestId}
                        style={props.labelStyle || undefined}
                        label={props.label}
                        required={props.required}
                    />
                )}
                <InputGroupConditionalWrapper
                    inputGroupAfter={props.inputGroupAfter}
                    inputGroupBefore={props.inputGroupBefore}
                    unitSuffix={props.unitSuffix}
                >
                    {props.inputGroupBefore}
                    {!props.mask && (
                        <FormControl
                            inputRef={
                                ref as
                                    | ((instance: HTMLInputElement) => void)
                                    | undefined
                            }
                            value={props.value}
                            autoFocus={props.autoFocus}
                            disabled={props.disabled}
                            placeholder={props.placeholder}
                            onChange={
                                props.onChange as
                                    | FormEventHandler<FormControl>
                                    | undefined
                            }
                            onKeyPress={
                                props.onKeyPress as
                                    | KeyboardEventHandler<FormControl>
                                    | undefined
                            }
                            onBlur={
                                props.onBlur as
                                    | FocusEventHandler<FormControl>
                                    | undefined
                            }
                            maxLength={props.maxLength}
                            name={props.name}
                            type={props.type}
                            autoComplete={props.autocomplete}
                            data-test-id={inputPrefixTestId}
                            as={StyledInput}
                            onFocus={props.onFocus}
                            isValid={isValid}
                            isInvalid={isInvalid}
                        />
                    )}
                    {props.mask && (
                        <StyledInputMask
                            className={classNames({
                                'form-control': true,
                                [styles.input]: props.disabled,
                                'is-valid': isValid,
                                'is-invalid': isInvalid,
                            })}
                            data-test-id={inputPrefixTestId}
                            inputRef={ref}
                            value={props.value}
                            disabled={props.disabled}
                            placeholder={
                                props.placeholder || _getPlaceHolder(props.mask)
                            }
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const onChange = props.onChange as
                                    | ChangeEventHandler<HTMLInputElement>
                                    | undefined;
                                if (onChange) {
                                    if (saveUnmasked) onChange(_unMask(e));

                                    onChange(e);
                                }
                            }}
                            onKeyPress={
                                props.onKeyPress as
                                    | KeyboardEventHandler<HTMLInputElement>
                                    | undefined
                            }
                            onBlur={
                                props.onBlur as
                                    | FocusEventHandler<HTMLInputElement>
                                    | undefined
                            }
                            mask={_getMask(props.mask)}
                            name={props.name}
                            type={props.type}
                        />
                    )}
                    {props.inputGroupAfter}
                    {props.unitSuffix && (
                        <span style={{ marginLeft: '10px' }}>
                            {props.unitSuffix}
                        </span>
                    )}
                </InputGroupConditionalWrapper>
                {isInvalid && validation?.message && (
                    <Form.Control.Feedback
                        type={validationFeedbackType}
                        style={{ display: 'block' }}
                    >
                        {validation.message}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
        );
    }
);

export default FormTextInput;

function InputGroupConditionalWrapper(props: {
    inputGroupBefore: ReactNode | undefined;
    inputGroupAfter: ReactNode | undefined;
    children: ReactNode | undefined;
    unitSuffix: ReactNode | undefined;
}) {
    if (props.inputGroupBefore || props.inputGroupAfter) {
        return (
            <InputGroup style={{ width: '100%' }}>{props.children}</InputGroup>
        );
    }
    if (props.unitSuffix) {
        return (
            <span style={{ display: 'flex', alignItems: 'baseline' }}>
                {props.children}
            </span>
        );
    }
    return <span>{props.children}</span>;
}

function _getPlaceHolder(maskName: string) {
    switch (maskName) {
        case 'phone': {
            return '(___) ___-____';
        }
        case 'date': {
            return '__/__';
        }
        default: {
            return undefined;
        }
    }
}

function _getMask(maskName: string) {
    switch (maskName) {
        case 'phone': {
            return '(999) 999-9999';
        }
        case 'date': {
            return '99/99';
        }
        case 'sscc': {
            return '(99) 9 9999999999999999 9';
        }
        default: {
            return '';
        }
    }
}

function _unMask(e: ChangeEvent<HTMLInputElement>) {
    return {
        ...e,
        target: {
            ...e.target,
            value: e.target.value.replace(/\D/g, ''),
        },
    };
}
